/**
 * A kind of document `GET documents` lists on its own, sent as a repeated `category` parameter (#6710).
 * The server matches each one exactly on a document's topics; several widen the list.
 */
export enum DocumentCategory {
  Policies = "Policies",
  Conflicts = "Conflicts",
  Proposals = "Proposals",
  Work = "Work",
  Chat = "Chat",
  KnowledgeBase = "KnowledgeBase",
}

/**
 * Whose documents a list holds, measured against the reader: their own uploads or everybody else's,
 * or those of the reader's legal entity or of any other (#7069).
 */
export enum DocumentOwner {
  Mine = "Mine",
  Others = "Others",
  MyOrganization = "MyOrganization",
  OtherOrganizations = "OtherOrganizations",
}

/**
 * The entity class of the topic that files a document under a proposal. The server's proposal field
 * permissions and the Proposals category read it; uploads used to write a bare "Proposal".
 */
export const PROPOSAL_TOPIC_ENTITY_CLASS = "Work.Proposal";

/**
 * The topic values that place a document in each category, as `GET documents` matches them: exactly,
 * never as a substring (#6710). The server's `DocumentCategoryTopics` holds the same values, and a
 * row's Type is worked out from these so it names the chips the row is listed under (#6720).
 */
const POLICY_CONTEXTS: readonly string[] = ["Policy", "ClientPolicy", "VendorPolicy", "PolicyTemplate"];
const CONFLICTS_CONTEXTS: readonly string[] = ["Conflicts", "ConflictsTemplate"];
const PROPOSAL_ENTITY_CLASSES: readonly string[] = [PROPOSAL_TOPIC_ENTITY_CLASS, "Proposal"];

type CategoryTopic = Readonly<{ entityClass?: string; context?: string }>;

const isInCategory: Readonly<Record<DocumentCategory, (topic: CategoryTopic) => boolean>> = {
  [DocumentCategory.Policies]: (topic) => POLICY_CONTEXTS.includes(topic.context ?? ""),
  [DocumentCategory.Conflicts]: (topic) => CONFLICTS_CONTEXTS.includes(topic.context ?? ""),
  [DocumentCategory.Proposals]: (topic) => PROPOSAL_ENTITY_CLASSES.includes(topic.entityClass ?? ""),
  [DocumentCategory.Work]: (topic) => topic.entityClass === "Work.Agreement" && topic.context === "WorkDocuments",
  [DocumentCategory.Chat]: (topic) => topic.entityClass === "Messaging.Attachment" && topic.context === "CopiedFrom",
  [DocumentCategory.KnowledgeBase]: (topic) => topic.entityClass === "KnowledgeBaseArticle",
};

/** The categories a document with `topics` is listed under, in the order of the enum. A document can be in several. */
export function documentCategoriesOf(topics: readonly CategoryTopic[]): DocumentCategory[] {
  return Object.values(DocumentCategory).filter((category) => topics.some(isInCategory[category]));
}
